import { Command } from 'commander';
import chalk from 'chalk';
import { join, relative } from 'path';
import { ApiClient } from '../client.js';
import { confirm } from '../prompt.js';
import { detectWorkspace, type Workspace } from '../config-as-code/workspace.js';
import { readRepoConfig } from '../config-as-code/repo-config.js';
import { requireActiveOrg } from '../require-active-org.js';
import { assertDatabaseMatchesBinding, autoBindIfUnbound, isValidDatabaseId } from '../database-binding.js';
import { resolveTargetFolder, targetDatabaseId } from '../config-as-code/resolve.js';
import { getConfig, getDatabaseRecord, metaFromRecord, type DatabaseRecord } from '../config-as-code/api.js';
import { markProductionMirror, writeDatabaseFolder } from '../config-as-code/config-writer.js';

/**
 * Materialize a production database as a read-only mirror folder (`rekor-ws/databases/<prodId>/`) so
 * the live production config is browsable alongside an editable preview. Best-effort and non-fatal:
 * a preview pull has already succeeded by the time this runs, so any failure (deleted/inaccessible
 * production, or a disk-write error) only prints a note. Never binds the worktree — a mirror is not
 * a push target. Pass `preFetchedRecord` when the caller already has the production record (the
 * direct `pull <production>` path) to avoid a redundant fetch.
 */
async function writeProductionMirror(
  client: ApiClient,
  ws: Workspace,
  prodId: string,
  preFetchedRecord?: DatabaseRecord,
): Promise<void> {
  // Defense-in-depth: `prodId` is server-provided and becomes a filesystem path segment below. Refuse
  // a malformed id (matches the worktree-binding guard) — skip-with-warn, honoring the best-effort contract.
  if (!isValidDatabaseId(prodId)) {
    console.warn(chalk.dim(`  Note: skipping production mirror — '${prodId}' is not a valid database id.`));
    return;
  }
  try {
    const record = preFetchedRecord ?? (await getDatabaseRecord(client, prodId));
    const config = await getConfig(client, prodId);
    const folder = join(ws.databasesDir, prodId);
    // Always a production database here (direct prod pull, or a preview's origin) — hard-set the
    // environment so the read-only discriminator holds even if the record's field were ever absent.
    const meta = { ...metaFromRecord(record, prodId), environment: 'production' };
    writeDatabaseFolder(folder, meta, config);
    markProductionMirror(folder, prodId);
    console.log(chalk.dim(`  Mirrored production '${prodId}' → ${relative(ws.gitRoot, folder)} (read-only).`));
  } catch (err) {
    console.warn(
      chalk.dim(`  Note: could not mirror production '${prodId}' (${err instanceof Error ? err.message : String(err)}). The preview pull is unaffected.`),
    );
  }
}

export const pullCommand = new Command('pull')
  .description('Pull a database config into rekor-ws/ as YAML files (a preview, plus a read-only mirror of linked production)')
  .argument('[database]', 'Preview (editable) or production (read-only mirror) database id or folder; defaults to the enclosing / only / default folder')
  .option('-y, --yes', 'Skip the overwrite confirmation')
  .action(async (selector: string | undefined, _opts, command: Command) => {
    const opts = command.optsWithGlobals() as { database?: string; yes?: boolean };
    const sel = selector ?? opts.database;

    await requireActiveOrg();

    const ws = detectWorkspace();
    if (!ws) throw new Error('Not inside a git repository — rekor-ws lives at the repo root.');

    const repo = readRepoConfig(ws);
    const target = resolveTargetFolder(ws, sel, repo?.default_database);
    const id = targetDatabaseId(target, sel);
    if (!id) throw new Error(`Could not determine the database id for ${target.folder}. Pass the preview id explicitly.`);

    const client = new ApiClient();
    const record = await getDatabaseRecord(client, id);
    if (record.environment === 'production') {
      // A directly-targeted production database materializes as a read-only mirror (not editable, not
      // bound). Returns before the binding assert + overwrite confirm — a mirror is always safe to
      // refresh. Reuse the record we already fetched rather than re-fetching it inside the helper.
      await writeProductionMirror(client, ws, record.id ?? id, record);
      return;
    }

    // Routing guard: fail before overwriting this worktree's local files with another db's config.
    assertDatabaseMatchesBinding(id);

    if (target.exists && !opts.yes) {
      const ok = await confirm(`Overwrite local files in ${relative(ws.gitRoot, target.folder)} with the server config for '${id}'?`);
      if (!ok) {
        console.log('Aborted.');
        return;
      }
    }

    const config = await getConfig(client, id);
    writeDatabaseFolder(target.folder, metaFromRecord(record, id), config);

    const counts = [
      `${config.collections.length} collection(s)`,
      `${config.relationship_types.length} relationship-type(s)`,
      `${config.inbound_webhooks.length} inbound webhook(s)`,
      `${config.triggers.length} trigger(s)`,
      `${config.toolsets.length} toolset(s)`,
      `${config.tools.length} tool(s)`,
    ].join(', ');
    console.log(chalk.green(`Pulled '${id}' → ${relative(ws.gitRoot, target.folder)}`));
    console.log(`  ${counts}`);
    console.log(chalk.dim('  Secrets are not written. Manage them with `rekor secrets` / `rekor inbound-webhooks` / `rekor triggers`.'));

    autoBindIfUnbound(id);

    // Mirror the linked production config read-only alongside the preview, so the live production
    // config is browsable for reference/diffing. Best-effort — the preview pull already succeeded.
    if (record.origin_database_id) {
      await writeProductionMirror(client, ws, record.origin_database_id);
    }
  });
